import React, {useEffect} from "react";
import {Session} from "../types/sessionsstate.ts";
import {confirmBox, error, IMAGES, KButton, KBUTTON_SIZE, KBUTTON_VARIANT, KSpace, Translate} from "@kopjra/uikit";
import {Col, Row} from "react-bootstrap";
import {buildStyles, CircularProgressbarWithChildren} from "react-circular-progressbar";
import {loadFromLocalStorage, tryCall} from "../utils/commons.ts";
import {Routes} from "../utils/router.tsx";
import {useTranslation} from "react-i18next";
import {useAbortSessionMutation, useCreateSessionMutation} from "../api/session.ts";
import {DangerHtml} from "./DangerHtml.tsx";

const TIMEOUT = 360;

export interface Props {
    session?: Session;
}

export const SessionLoader: React.FC<Props> = ({session}) => {

    const [triggerCreateSession] = useCreateSessionMutation();
    const [triggerAbortSession] = useAbortSessionMutation();
    const [timerVisible, setTimerVisible] = React.useState<boolean>(false);
    const [innerTimer, setInnerTimer] = React.useState<number>(0);

    function formatTime(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        // Pad with leading zeros if needed
        const minsStr = mins.toString().padStart(2, '0');
        const secsStr = secs.toString().padStart(2, '0');
        return `${minsStr}:${secsStr}`;
    }

    useEffect(() => {
        const timer = setInterval(() => {
            setInnerTimer((prev) => {
                if (prev <= TIMEOUT) {
                    return prev + 1;
                } else {
                    clearInterval(timer);
                    return TIMEOUT + 1;
                }
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const {t} = useTranslation();

    return (
        <div style={{position: "absolute", top: 0, left: 0, right: 0, minHeight: "100vh", display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            overflowY: "auto", backgroundColor: "#363D49"}} className={innerTimer <= TIMEOUT ? "guacDisplay" : "errorDisplay"}>
            <KButton text={<><i className="fal fa-lg fa-times"/>&nbsp;&nbsp;<Translate i18nKey="bar.cancelbutton"/></>}
                     variant={KBUTTON_VARIANT.secondary}
                     onClick={async () => {
                         if (await confirmBox({
                             dark: true,
                             message: t("new.abortSession"),
                             yesText: t("new.yes"),
                             noText: t("new.no"),
                         })) {
                             if (session?.id) {
                                 await tryCall(triggerAbortSession(session.id).unwrap());
                                 Routes.goSessions();
                             }
                         }
                     }}
                     size={KBUTTON_SIZE.xs}
                     style={{position: "fixed", top: 16, right: 17}}
            />
            <div className="text-center">
                <div className="text-center d-xxl-none">
                    <KSpace spaces={2}/>
                    <Row>
                        <Col className="text-center">
                            <div style={{width: 126, height: 126, display: "inline-block"}} >
                                <CircularProgressbarWithChildren maxValue={TIMEOUT} value={innerTimer} strokeWidth={3}
                                                                 styles={buildStyles({
                                                                     pathColor: innerTimer < TIMEOUT ? "#5198F4" : "#EF615E",
                                                                     trailColor: "rgba(54, 61, 73, 0)",
                                                                 })}>
                                    <img
                                        alt="logo_big"
                                        src={IMAGES["web-forensics"].still.large.dark}
                                        height={77}
                                    />
                                </CircularProgressbarWithChildren>
                            </div>
                        </Col>
                    </Row>
                    {timerVisible ? (
                        <Row>
                            <Col className="text-center mt-2">
                                <span style={{color: "#8C9AB1", fontSize: "13px", width: "100%"}}>{formatTime(innerTimer)}</span>
                            </Col>
                        </Row>
                    ) : null}
                    {innerTimer <= TIMEOUT ? (
                        <>
                            <Row>
                                <Col xl={9} lg={10} md={11} sm={12} className="text-center">
                                <span style={{color: "#5198F4", fontSize: "13px", width: "100%"}} ><DangerHtml
                                    html={t("new.waitingMessage")}/></span>
                                </Col>
                            </Row>
                            <KSpace spaces={2}/>
                            <Row>
                                <Col className="text-center">
                                <span style={{color: "#4e596b", fontSize: "18px", width: "100%", textTransform: "uppercase"}} onClick={(me) => {
                                    if (me.altKey && me.shiftKey) {
                                        setTimerVisible(!timerVisible);
                                    }
                                }}><DangerHtml
                                    html={t("new.discover")}/></span>
                                </Col>
                            </Row>
                            <KSpace spaces={0.5}/>
                            <Row>
                                <Col xl={9} lg={10} md={11} sm={12}>
                                    <iframe width="100%" height="400" src="https://www.youtube.com/embed/omk68q56CgM?si=6QHQOBupLe3asceK?color=white&amp;modestbranding=1&amp;rel=0&amp;controls=0" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe>
                                </Col>
                            </Row>
                        </>
                    ) : null}
                    {/*<TipCarousel style={{marginTop: 150}}/>*/}
                </div>
                <div className="text-center d-none d-xxl-block">
                    <KSpace spaces={2}/>
                    <Row onClick={(me) => {
                        if (me.altKey && me.shiftKey) {
                            setTimerVisible(!timerVisible);
                        }
                    }}>
                        <Col className="text-center">
                            <div style={{width: 126, height: 126, display: "inline-block"}}>
                                <CircularProgressbarWithChildren maxValue={TIMEOUT} value={innerTimer} strokeWidth={3}
                                                                 styles={buildStyles({
                                                                     pathColor: innerTimer < TIMEOUT ? "#5198F4" : "#EF615E",
                                                                     trailColor: "rgba(54, 61, 73, 0)",
                                                                 })}>
                                    <img
                                        alt="logo_big"
                                        src={IMAGES["web-forensics"].still.large.dark}
                                        height={77}
                                    />
                                </CircularProgressbarWithChildren>
                            </div>
                        </Col>
                    </Row>
                    {timerVisible ? (
                        <Row>
                            <Col className="text-center mt-2">
                                <span style={{color: "#8C9AB1", fontSize: "13px", width: "100%"}}>{formatTime(innerTimer)}</span>
                            </Col>
                        </Row>
                    ) : null}
                    {innerTimer <= TIMEOUT ? (
                        <>
                            <KSpace spaces={3}/>
                            <Row>
                                <Col lg={10} md={11} sm={12} className="text-center">
                                <span style={{color: "#5198F4", fontSize: "13px", width: "100%"}}><DangerHtml
                                    html={t("new.waitingMessage")} /></span>
                                </Col>
                            </Row>
                            <KSpace spaces={4}/>
                            <Row>
                                <Col className="text-center">
                                <span style={{color: "#4e596b", fontSize: "18px", width: "100%", textTransform: "uppercase"}} onClick={(me) => {
                                    if (me.altKey && me.shiftKey) {
                                        setTimerVisible(!timerVisible);
                                    }
                                }}><DangerHtml
                                    html={t("new.discover")}/></span>

                                </Col>
                            </Row>
                            <KSpace spaces={0.5}/>
                            <Row>
                                <Col lg={10} md={11} sm={12}>
                                    <iframe width="100%" height="400" src="https://www.youtube.com/embed/omk68q56CgM?si=6QHQOBupLe3asceK?color=white&amp;modestbranding=1&amp;rel=0&amp;controls=0" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe>
                                </Col>
                            </Row>
                        </>
                    ) : null}
                    {/*<TipCarousel style={{marginTop: 150}}/>*/}
                </div>
                {innerTimer > TIMEOUT ? (
                    <>
                        <KSpace spaces={3}/>
                        <Row>
                            <Col xl={9} lg={10} md={11} sm={12} className="text-center">
                                <span style={{color: "#8C9AB1", fontSize: "18px", width: "100%"}}><DangerHtml html={t("new.errorMessage")}/></span>
                            </Col>
                        </Row>
                        <KSpace spaces={3}/>
                        <Row>
                            <Col xl={9} lg={10} md={11} sm={12} className="text-center">
                                <KButton text={<><i className="fal fa-lg fa-redo"/>&nbsp;&nbsp;<Translate i18nKey="new.retryButton"/></>}
                                         variant={KBUTTON_VARIANT.primary}
                                         onClick={async () => {
                                             if (session) {
                                                 await tryCall(triggerCreateSession({
                                                     sessionInput: {
                                                         name: session.name,
                                                         fullProxy: session.fullProxy,
                                                     },
                                                     customization: loadFromLocalStorage("customization") || undefined,
                                                 }).unwrap(), {
                                                     error: {
                                                         402: t("error.paymentRequired"),
                                                         default: t("error.sessionCreation"),
                                                     }
                                                 }, (newSession) => {
                                                     if (newSession) {
                                                         Routes.goNavigate(`${newSession.id}`, true);
                                                     } else {
                                                         error(t("error.sessionCreation"));
                                                     }
                                                 });
                                             }
                                         }}
                                         size={KBUTTON_SIZE.lg}
                                />
                            </Col>
                        </Row>
                    </>
                ) : null}
            </div>
        </div>
    )
}